import { computed, unref, watchPostEffect } from "vue";
import { u as useDomRect } from "./useDomRect-32e0f717.mjs";
import { u as useDocument } from "./useDocument-5c9446c6.mjs";
import { u as usePlacementAliases } from "./usePlacementAliases-c092b4d3.mjs";
import { u as unwrapEl } from "../utils/unwrapEl-55936fea.mjs";
import { m as mapObject } from "../utils/map-object-d5031296.mjs";
const coordsToCss = ({ x, y }) => ({ left: `${x}px`, top: `${y}px` });
const parseOffset = (offset) => {
  return Array.isArray(offset) ? { main: offset[0], cross: offset[1] } : { main: offset, cross: 0 };
};
const calculateContentAlignment = (align, anchorStart, anchorSize, contentSize) => {
  if (align === "start") {
    return anchorStart;
  }
  if (align === "end") {
    return anchorStart + anchorSize - contentSize;
  }
  return anchorStart + (anchorSize - contentSize) / 2;
};
const calculateContentCoords = (position, align, anchor, content) => {
  const alignmentX = calculateContentAlignment(align, anchor.left, anchor.width, content.width);
  const alignmentY = calculateContentAlignment(align, anchor.top, anchor.height, content.height);
  switch (position) {
    case "top":
      return { x: alignmentX, y: anchor.top - content.height };
    case "left":
      return { y: alignmentY, x: anchor.left - content.width };
    case "right":
      return { y: alignmentY, x: anchor.right };
    case "bottom":
    default:
      return { x: alignmentX, y: anchor.bottom };
  }
};
const calculateOffsetCoords = (position, offset) => {
  const { main, cross } = parseOffset(offset);
  switch (position) {
    case "left":
      return { y: cross, x: -main };
    case "right":
      return { y: cross, x: main };
    case "top":
      return { y: -main, x: cross };
    case "bottom":
    default:
      return { y: main, x: cross };
  }
};
const calculateContentOverflow = (coords, content, viewport) => {
  const xMax = viewport.right;
  const yMax = viewport.bottom;
  const xMin = viewport.left;
  const yMin = viewport.top;
  return {
    top: Math.max(yMin - coords.y, 0),
    bottom: Math.max(coords.y + content.height - yMax, 0),
    left: Math.max(xMin - coords.x, 0),
    right: Math.max(coords.x + content.width - xMax, 0)
  };
};
const clamp = (min, v, max) => Math.max(Math.min(v, max), min);
const calculateClipToEdge = (coords, offsetCoords, content, anchor, viewport) => {
  const { top, bottom, left, right } = calculateContentOverflow(coords, content, viewport);
  const x = coords.x - right + left;
  const y = coords.y - bottom + top;
  const { x: offsetX, y: offsetY } = offsetCoords;
  return {
    // Clamp content position near anchor, so any content edge should touch anchor edge
    x: clamp(anchor.left + offsetX - content.width, x, anchor.right + offsetX),
    y: clamp(anchor.top + offsetY - content.height, y, anchor.bottom + offsetY)
  };
};
const getAutoPlacement = (position, align, coords, content, viewport) => {
  const overflow = calculateContentOverflow(coords, content, viewport);
  const convertPlacement = (position2, align2) => ({ position: position2, align: align2 });
  const newPlacements = {
    top: "bottom",
    bottom: "top",
    right: "left",
    left: "right"
  };
  if (!overflow[position]) {
    return convertPlacement(position, align);
  }
  const newPlacement = newPlacements[position];
  if (newPlacement === "bottom" || newPlacement === "top") {
    if (overflow.left) {
      return convertPlacement(newPlacement, "start");
    }
    if (overflow.right) {
      return convertPlacement(newPlacement, "end");
    }
  }
  if (newPlacement === "left" || newPlacement === "right") {
    if (overflow.top) {
      return convertPlacement(newPlacement, "start");
    }
    if (overflow.bottom) {
      return convertPlacement(newPlacement, "end");
    }
  }
  return convertPlacement(newPlacement, "center");
};
const findFirstRelativeParent = (el) => {
  while (el) {
    const positionValue = window.getComputedStyle(el).getPropertyValue("position") || el.style.position;
    if (positionValue === "relative") {
      return el;
    }
    el = el.parentElement;
  }
  return document.body;
};
const useDropdown = (anchorRef, contentRef, options, props) => {
  const documentRef = useDocument();
  const rootRef = computed(() => {
    if (!documentRef.value) {
      return void 0;
    }
    const { root } = unref(options);
    if (root) {
      let el;
      if (typeof root === "string") {
        el = documentRef.value.querySelector(root);
      } else {
        el = root;
      }
      if (!el) {
        return documentRef.value.body;
      }
      return findFirstRelativeParent(el);
    }
    return documentRef.value.body;
  });
  const { domRect: anchorDomRect } = useDomRect(anchorRef);
  const { domRect: contentDomRect } = useDomRect(contentRef);
  const css = {
    position: "absolute"
  };
  const { position, align } = usePlacementAliases(props);
  watchPostEffect(() => {
    var _a;
    if (!rootRef.value || !anchorDomRect.value || !contentDomRect.value) {
      return;
    }
    const { offset, keepAnchorWidth, autoPlacement, stickToEdges } = unref(options);
    let coords = calculateContentCoords(position.value, align.value, anchorDomRect.value, contentDomRect.value);
    let offsetCoords = { x: 0, y: 0 };
    if (offset) {
      offsetCoords = calculateOffsetCoords(position.value, offset);
      coords = mapObject(coords, (c, key) => c + offsetCoords[key]);
    }
    const rootRect = rootRef.value.getBoundingClientRect();
    const viewportRect = ((_a = unref(options).viewport) == null ? void 0 : _a.getBoundingClientRect()) ?? rootRect;
    if (autoPlacement) {
      const { position: newPosition, align: newAlign } = getAutoPlacement(position.value, align.value, coords, contentDomRect.value, viewportRect);
      if (newPosition !== position.value || newAlign !== align.value) {
        coords = calculateContentCoords(newPosition, newAlign, anchorDomRect.value, contentDomRect.value);
        if (offset) {
          offsetCoords = calculateOffsetCoords(newPosition, offset);
          coords = mapObject(coords, (c, key) => c + offsetCoords[key]);
        }
      }
    }
    if (stickToEdges) {
      coords = calculateClipToEdge(coords, offsetCoords, contentDomRect.value, anchorDomRect.value, viewportRect);
    }
    coords.x -= rootRect.x + rootRef.value.clientLeft;
    coords.y -= rootRect.y + rootRef.value.clientTop;
    if (unwrapEl(contentRef.value)) {
      let widthCss = {};
      if (keepAnchorWidth) {
        const { width } = anchorDomRect.value;
        widthCss = { width: `${width}px`, maxWidth: `${width}px` };
      }
      Object.assign(unwrapEl(contentRef.value).style, {
        ...css,
        ...coordsToCss(coords),
        ...widthCss
      });
    }
  });
  return {
    anchorDomRect,
    contentDomRect
  };
};
export {
  useDropdown as u
};
//# sourceMappingURL=useDropdown-6aa8c138.mjs.map
